import { useState } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { ShieldCheck, UserCheck, MessageSquare, History, Sparkles } from "lucide-react";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FEATURES = [
  { icon: ShieldCheck, title: "Rollen & Teams", text: "Benutzer haben jetzt Rollen (Administrator, Manager, Mitarbeiter, Nur Lesen) und gehören zu Teams. Verwaltung unter „Benutzer“." },
  { icon: UserCheck, title: "Zuständigkeiten", text: "Weisen Sie Unternehmen einem zuständigen Mitarbeiter oder Team zu – direkt in der Kundendetailseite. Mitarbeiter sehen ihre eigenen Datensätze." },
  { icon: MessageSquare, title: "Kommentare & @Erwähnungen", text: "Schreiben Sie interne Kommentare an Datensätzen und erwähnen Sie Kollegen mit „@“ – sie bekommen eine Benachrichtigung (Glocke oben rechts)." },
  { icon: History, title: "Änderungsverlauf", text: "Der Tab „Verlauf“ zeigt, wer wann was geändert hat. Bei gleichzeitiger Bearbeitung warnt das System vor Konflikten." },
];

// forceOpen/onClose => manueller Modus (z.B. Button "Neuerungen ansehen" in den Einstellungen).
// Ohne Props => Auto-Modus: erscheint einmalig beim ersten Login und wird per onboarding_seen dauerhaft ausgeblendet.
export const OnboardingDialog = ({ forceOpen = false, onClose }) => {
  const { user, markOnboardingSeen } = useAuth();
  const [autoOpen, setAutoOpen] = useState(true);
  const [saving, setSaving] = useState(false);

  const isManual = typeof onClose === "function";

  if (isManual) {
    if (!forceOpen) return null;
  } else if (!user || user.onboarding_seen === "multiuser_v1") {
    return null;
  }

  const open = isManual ? forceOpen : autoOpen;

  const dismiss = async () => {
    if (isManual) {
      onClose();
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API_URL}/auth/onboarding-seen`);
    } catch (e) {
      console.debug("Onboarding-Status speichern fehlgeschlagen:", e);
    } finally {
      markOnboardingSeen();
      setAutoOpen(false);
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) dismiss(); }}>
      <DialogContent className="sm:max-w-lg" data-testid="onboarding-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Sparkles className="w-5 h-5 text-primary" />
            Willkommen im neuen Multi-User-CRM
          </DialogTitle>
          <DialogDescription>
            Das CRM wurde grundlegend erweitert. Hier die wichtigsten Neuerungen für die Zusammenarbeit im Team:
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          {FEATURES.map((f) => {
            const Icon = f.icon;
            return (
              <div key={f.title} className="flex gap-3" data-testid={`onboarding-feature-${f.title}`}>
                <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                  <Icon className="h-4.5 w-4.5" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-semibold">{f.title}</p>
                  <p className="text-sm text-muted-foreground">{f.text}</p>
                </div>
              </div>
            );
          })}
        </div>
        <DialogFooter>
          <Button onClick={dismiss} disabled={saving} className="w-full sm:w-auto" data-testid="onboarding-dismiss-btn">
            {saving ? "Einen Moment…" : (isManual ? "Schließen" : "Los geht's")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
